import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  Spinner,
} from "@fluentui/react-components";
import { CheckmarkRegular, DismissRegular } from "@fluentui/react-icons";
import { createUseCase, updateUseCase } from "api";
import { ConstantValues } from "common/constants";
import { DialogFooterAction } from "components/dialogFooterAction/DialogFooterAction";
import { IUseCase, IUseCaseCreate } from "models/IUseCase";
import { ChangeEvent, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import {
  useSharedDialogContentStyles,
  useSharedDialogFooterStyles,
} from "theme/sharedStyles";
import { addUseCase, updateUseCaseInSlice } from "../../../redux/useCasesSlice";
import { useEditUseCaseStyles } from "./EditUseCase.styles";
import { IEditUseCaseProps } from "./EditUseCase.types";

export const EditUseCase = (props: IEditUseCaseProps): JSX.Element => {
  const styles = useEditUseCaseStyles();
  const contentStyles = useSharedDialogContentStyles();
  const footerStyles = useSharedDialogFooterStyles();
  const [title, setTitle] = useState<string>(
    props.useCase?.title || ConstantValues.EMPTY_STRING,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const dispatch = useDispatch();
  const allUseCases: IUseCase[] = useSelector(
    (state: RootState) => state.useCasesSlice.value,
  );

  const handleTitleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setTitle(e.target.value);
    if (fieldErrors.title) {
      setFieldErrors((prev) => ({ ...prev, title: "" }));
    }
  };

  const validateFields = (): boolean => {
    const errors: Record<string, string> = {};

    if (!title.trim()) {
      errors.title = "Title is required";
    } else if (title.trim().length > 50) {
      errors.title = "Title cannot be more than 50 characters";
    } else {
      const isDuplicateTitle = allUseCases.some(
        (uc) =>
          uc.title.trim().toLowerCase() === title.trim().toLowerCase() &&
          uc.id !== props.useCase?.id,
      );
      if (isDuplicateTitle) {
        errors.title = "A use case with this title already exists";
      }
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return false;
    }

    setFieldErrors({});
    return true;
  };

  const handleSave = async (): Promise<void> => {
    if (!validateFields()) return;

    setIsLoading(true);
    try {
      let useCase: IUseCase;
      if (props.useCase) {
        useCase = { ...props.useCase, title };
        await updateUseCase({ id: props.useCase.id, usecase: useCase });
        dispatch(updateUseCaseInSlice(useCase));
      } else {
        const useCaseCreate: IUseCaseCreate = { title };
        useCase = await createUseCase(useCaseCreate);
        dispatch(addUseCase(useCase));
      }

      props.onDismiss(true);
    } catch (e) {
      console.error("Error saving use case:", e);
      setFieldErrors({
        general: "An error occurred while saving. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const hasIncompleteDetails = !title.trim();

  return (
    <Dialog
      open={props.isOpen}
      onOpenChange={(_, data) => {
        if (!data.open) props.onDismiss(false);
      }}
    >
      <DialogSurface className={footerStyles.dialogSurface}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                onClick={() => props.onDismiss(false)}
                disabled={isLoading}
              />
            }
          >
            {props.useCase ? "Update Use Case" : "Add Use Case"}
          </DialogTitle>
          <DialogContent className={styles.dialogContent}>
            <div className={contentStyles.formContainer}>
              {isLoading && (
                <div className={contentStyles.formLoadingOverlay}>
                  <Spinner label="Saving use case..." />
                </div>
              )}
              <Field
                label="Title"
                required
                validationState={fieldErrors.title ? "error" : "none"}
                validationMessage={fieldErrors.title}
              >
                <Input
                  value={title}
                  onChange={handleTitleChange}
                  placeholder="Enter use case title"
                  disabled={isLoading}
                />
              </Field>
              {fieldErrors.general && (
                <div className={contentStyles.feedbackError} role="alert">
                  {fieldErrors.general}
                </div>
              )}
            </div>
          </DialogContent>
        </DialogBody>
        {!isLoading && (
          <div className={footerStyles.dialogFooter}>
            <DialogFooterAction
              icon={<CheckmarkRegular />}
              onClick={handleSave}
              dimmed={hasIncompleteDetails}
              tooltip="Save"
            />
          </div>
        )}
      </DialogSurface>
    </Dialog>
  );
};
